import React, { useContext, useEffect, useState } from "react";
import { Plus as PlusIcon } from "@actinc/dls/icons";
import { Box, Button, Paper, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "urql";

import { UserContext } from "../../../userProvider";

import { DataGrid, NoAccess } from "../../../components";

import { disableAction, setTitle } from "../../../services/utils";

import { AdminProgramDetailsDialog } from "../ProgramDetailsDialog";
import { AdminProgramKeywordsDialog } from "../ProgramKeywordsDialog";

const AdminPrograms = () => {
  const user = useContext(UserContext);

  const navigate = useNavigate();

  const [dialogState, setDialogState] = useState({
    open: false,
    programID: null,
  });

  const [getProgramsResponse] = useQuery({
    query: getProgramsQuery,
  });

  useEffect(() => {
    setTitle(["CDE", "Admin", "Programs"]);
  }, []);

  if (!user?._actions?.includes("managePrograms"))
    return (
      <NoAccess message="You do not have permission to manage programs." />
    );

  const closeDialog = () => {
    setDialogState({ open: false, programID: null });
  };
  const openDialog = (name) => (selectedRow) => () => {
    setDialogState({ name, open: true, programID: selectedRow?.id });
  };

  const columns = [
    {
      field: "name",
      flex: 1,
      headerName: "Program Name",
      type: "string",
      sortComparator: (v1, v2) =>
        v1.localeCompare(v2, "en", { sensitivity: "base" }),
    },
    {
      field: "cdeID",
      headerName: "Program ID",
      type: "number",
      width: 120,
    },
    {
      field: "itemNamePrefix",
      headerName: "Item Name Identifier",
      type: "string",
      width: 190,
    },
  ];
  const dataGridProps = {
    actions: [
      {
        disabled: disableAction("manageDetails"),
        label: "Manage Details",
        onClick: openDialog("details"),
      },
      {
        disabled: disableAction("manageKeywords"),
        label: "Manage Keywords",
        onClick: openDialog("keywords"),
      },
      {
        disabled: disableAction("manageMetafiles"),
        label: "Manage Metafiles",
        onClick: (row) => () => navigate(`/admin/programs/${row.id}/metafiles`),
      },
      {
        disabled: disableAction("manageReviewGroups"),
        label: "Manage Review Groups",
        onClick: (row) => () =>
          navigate(`/admin/programs/${row.id}/reviewGroups`),
      },
    ],
    columns,
    error: getProgramsResponse?.error?.message,
    loading: getProgramsResponse?.fetching,
    rows: getProgramsResponse?.data?.programs,
    sortModel: [
      {
        field: "name",
        sort: "asc",
      },
    ],
  };
  const keywordsDialogProps = {
    closeDialog,
    programID: dialogState.programID,
  };
  const settingsDialogProps = {
    closeDialog,
    programID: dialogState.programID,
    programs: getProgramsResponse?.data?.programs || [],
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginX: 2,
          marginY: 1,
        }}
      >
        <Typography variant="h4">Manage Programs</Typography>
        <Button
          onClick={openDialog("details")(null)}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create Program
        </Button>
      </Box>
      <Paper
        elevation={1}
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          marginX: 2,
          marginY: 1,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          <DataGrid {...dataGridProps}></DataGrid>
        </Box>
      </Paper>
      {dialogState.open && dialogState.name === "details" && (
        <AdminProgramDetailsDialog {...settingsDialogProps} />
      )}
      {dialogState.open && dialogState.name === "keywords" && (
        <AdminProgramKeywordsDialog {...keywordsDialogProps} />
      )}
    </>
  );
};

// TODO: Adjust request once Tone is done with changes (including category, etc.)
const getProgramsQuery = `
  query GetPrograms {
    programs {
      id
      cdeID
      itemNamePrefix
      name
      _actions
    }
  }`;

export { AdminPrograms, getProgramsQuery };
